import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n, useT } from "@/i18n";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Header() {
  const t = useT();
  const { lang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open]);

  const onHome = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;
  const homeHref = `/${lang}`;

  const anchor = (id) => (onHome ? `#${id}` : `${homeHref}#${id}`);

  const navItems = [
    { key: "about", id: "about" },
    { key: "howItWorks", id: "how" },
    { key: "benefits", id: "benefits" },
    { key: "faq", id: "faq" },
    { key: "contact", id: "contact" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        scrolled || open
          ? "bg-[#0B0F14]/85 backdrop-blur border-b border-white/5"
          : "bg-transparent"
      }`}
      data-testid="site-header"
      style={{ wordBreak: "normal" }}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-[#C9F14B] focus:text-[#0B0F14] focus:px-3 focus:py-1 focus:rounded"
      >
        {t("a11y.skipToContent")}
      </a>
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="h-[68px] flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1" aria-label="primary">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={anchor(item.id)}
                data-testid={`nav-${item.key}`}
                className="px-3 py-2 text-[13px] tracking-wide text-[#c8cdd6] hover:text-white transition-colors"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher compact />
            <a
              href={anchor("contact")}
              data-testid="nav-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#C9F14B] text-[#0B0F14] px-4 py-2 text-[13px] font-medium hover:bg-[#d7ff5f] transition-colors"
            >
              {t("cta.secondary")}
            </a>
          </div>
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 w-10 h-10 text-[#F1ECDE]"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-x-0 top-[68px] bottom-0 z-40 bg-[#0B0F14] border-t border-white/5 transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!open}
        data-testid="mobile-drawer"
      >
        <div className="h-full overflow-y-auto px-6 py-8 flex flex-col">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.key}>
                <a
                  href={anchor(item.id)}
                  data-testid={`mobile-nav-${item.key}`}
                  className="block py-3 text-lg text-[#F1ECDE] border-b border-white/5"
                  onClick={() => setOpen(false)}
                >
                  {t(`nav.${item.key}`)}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.24em] text-[#8f96a3]">
              {t("nav.language")}
            </span>
            <LanguageSwitcher />
          </div>
          <a
            href={anchor("contact")}
            onClick={() => setOpen(false)}
            className="mt-8 inline-flex items-center justify-center rounded-full bg-[#C9F14B] text-[#0B0F14] px-5 py-3 font-medium"
            data-testid="mobile-cta"
          >
            {t("cta.secondary")}
          </a>
          <div className="mt-auto pt-8 text-[11px] text-[#8f96a3]">
            © 2026 Zenithradiar S.A.S.
          </div>
        </div>
      </div>
    </header>
  );
}
